import React, { useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Prompt } from 'react-router-dom';
import { initialState, reducer } from '../reducers/header/containerReducer';
import { isValid, useDirtyFieldValidation } from '@totalsoft/pure-validations-react';
import { financialProductWarrantyValidator } from '../../validators';
import { useToast } from 'hooks/toasts/useToast';
import { useQuery, useMutation, useSubscription } from 'hooks/apollo';
import { ADD_UPDATE_FINANCIAL_PRODUCT_WARRANTY } from '../../mutations';
import { FINANCIAL_PRODUCT_WARRANTY_UPDATED } from '../../subscriptions';
import { FINANCIAL_PRODUCT_WARRANTY_TYPES_QUERY, FINANCIAL_PRODUCT_WARRANTY_QUANTITY_TYPES_QUERY } from '../../queries';
import ContentPaste from '@mui/icons-material/ContentPaste';
import FinancialProductWarrantiesEditHeaderComponent from './FinancialProductWarrantiesEditHeaderComponent';
import { Card } from '@totalsoft/rocket-ui';

const FinancialProductWarrantiesEditHeaderContainer = ({
  canWrite,
  data,
  productId,
  leasingFinancialProductXWarrantyId,
  onRedirect,
  onChangeTitle
}) => {
  const addToast = useToast();
  const [t] = useTranslation();

  const [localState, dispatch] = useReducer(reducer, initialState);
  const [financialProductWarrantyValidation, validateFinancialProductWarranty] = useDirtyFieldValidation(financialProductWarrantyValidator);

  const [addOrUpdateFinancialProductWarranty] = useMutation(ADD_UPDATE_FINANCIAL_PRODUCT_WARRANTY, {
    onError: () => {
      dispatch({ type: 'OnHeaderSaved' });
    }
  });

  const { data: warrantyTypesData } = useQuery(FINANCIAL_PRODUCT_WARRANTY_TYPES_QUERY);

  const { data: warrantyQuantityTypesData } = useQuery(FINANCIAL_PRODUCT_WARRANTY_QUANTITY_TYPES_QUERY);

  useSubscription(FINANCIAL_PRODUCT_WARRANTY_UPDATED, {
    onData: ({ data: { data }, client }) => {
      addToast(t('FinancialProduct.Details.Warranties.FinancialProductWarrantyUpdated'), 'success');
      dispatch({ type: 'OnHeaderSaved' });
      if (leasingFinancialProductXWarrantyId === undefined)
        onRedirect(data.financialProductWarrantyUpdated.financialProductWarranty.leasingFinancialProductXWarrantyId);
      client.cache.evict({
        id: `FinancialProductItem:{"productId":${data.financialProductWarrantyUpdated.financialProductWarranty.productId}}`
      });

      client.cache.evict({
        id: `FinancialProductWarrantyEditItem:{"leasingFinancialProductXWarrantyId":${data.financialProductWarrantyUpdated.financialProductWarranty.leasingFinancialProductXWarrantyId}}`
      });
      client.cache.gc();
    }
  });

  const handlePropertyChanged = (propertyName, value) => {
    dispatch({ type: 'OnHeaderPropertyChanged', propertyName, value });
  };

  const handleSave = () => {
    if (!validateFinancialProductWarranty(localState)) return;

    const { ...financialProductWarranty } = localState;
    const input = {
      leasingFinancialProductXWarrantyId: financialProductWarranty.leasingFinancialProductXWarrantyId,
      productId: financialProductWarranty.productId,
      warrantyTypeId: parseInt(financialProductWarranty.warrantyTypeId),
      quantity: parseInt(financialProductWarranty.quantity),
      quantityTypeId: parseInt(financialProductWarranty.quantityTypeId),
      mandatory: financialProductWarranty.mandatory,
      templateId: financialProductWarranty.genericFilterTemplateId,
      templateName: financialProductWarranty.templateName
    };

    dispatch({ type: 'OnHeaderSaveStarted' });
    return addOrUpdateFinancialProductWarranty({ variables: { input } });
  };

  useEffect(() => {
    if (data?.warrantyTypeName) {
      onChangeTitle(data.warrantyTypeName);
    }
  }, [data?.warrantyTypeName, onChangeTitle]);

  useEffect(() => {
    if (!(financialProductWarrantyValidation |> isValid)) validateFinancialProductWarranty(localState);
  }, [localState, validateFinancialProductWarranty, financialProductWarrantyValidation]);

  useEffect(() => {
    if (data) dispatch({ type: 'OnInitState', data });
    else dispatch({ type: 'OnAdded', productId });
  }, [data, productId]);

  return warrantyTypesData?.financialProductWarrantyTypeList && warrantyQuantityTypesData?.financialProductWarrantyQuantityTypeList ? (
    <Card icon={ContentPaste} iconColor='primary' title={t('FinancialProduct.Details.Warranties.EditTitle')}>
      <Prompt
        when={leasingFinancialProductXWarrantyId !== undefined && localState.changed}
        message={t('FinancialProduct.Details.Warranties.Header.LeavingWithoutSaving')}
      />
      <FinancialProductWarrantiesEditHeaderComponent
        canWrite={canWrite}
        warranty={localState}
        onSave={handleSave}
        onPropertyChanged={handlePropertyChanged}
        validation={financialProductWarrantyValidation}
        warrantyTypes={warrantyTypesData?.financialProductWarrantyTypeList}
        quantityTypes={warrantyQuantityTypesData?.financialProductWarrantyQuantityTypeList}
      />
    </Card>
  ) : (
    <></>
  );
};

FinancialProductWarrantiesEditHeaderContainer.propTypes = {
  canWrite: PropTypes.bool.isRequired,
  data: PropTypes.object,
  productId: PropTypes.number.isRequired,
  leasingFinancialProductXWarrantyId: PropTypes.number,
  onRedirect: PropTypes.func,
  onChangeTitle: PropTypes.func
};

export default FinancialProductWarrantiesEditHeaderContainer;
